import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { WearableObservationSectionId } from "@/utils/wearableObservationFormat";
import { buildPeriodSeries } from "../periodAggregation";
import { MetricTrendCard } from "./MetricTrendCard";
import type { PeriodChartStyle } from "./PeriodChartStyleToggle";
import { SECTION_META } from "./ObservationSection";

interface PeriodTrendsViewProps {
  /** Observations já filtradas pelo período selecionado. */
  entries: any[];
  /** Define quando o gráfico deve permitir rolagem horizontal. */
  rangeDays: number;
  /** Fim do período (YYYY-MM-DD) — completa o eixo do tempo de tela. */
  periodEndKey: string;
  /** Linha ou barras — o gráfico fica interno em cada cartão de métrica. */
  chartStyle?: PeriodChartStyle;
  /** Seletor Linha/Barras exibido na mesma faixa dos filtros. */
  chartStyleControl?: ReactNode;
  /** Abre a visão detalhada de um dia específico. */
  onSelectDay: (dateKey: string) => void;
}

/**
 * Visão de período: todas as métricas do FluxHealth (mesmo conjunto em
 * linha e barras), cada uma com seu gráfico interno.
 */
export function PeriodTrendsView({
  entries,
  rangeDays,
  periodEndKey,
  chartStyle = "line",
  chartStyleControl,
  onSelectDay,
}: PeriodTrendsViewProps) {
  const [selectedSection, setSelectedSection] = useState<
    WearableObservationSectionId | "all"
  >("all");
  const seriesList = useMemo(
    () => buildPeriodSeries(entries, rangeDays, periodEndKey),
    [entries, rangeDays, periodEndKey]
  );

  const sections = useMemo(
    () =>
      SECTION_META.map((meta) => ({
        ...meta,
        series: seriesList.filter((series) => series.sectionId === meta.id),
      })).filter((section) => section.series.length > 0),
    [seriesList]
  );

  // Se a seção selecionada deixar de existir no novo período, volta para Todos.
  useEffect(() => {
    if (
      selectedSection !== "all" &&
      !sections.some((section) => section.id === selectedSection)
    ) {
      setSelectedSection("all");
    }
  }, [sections, selectedSection]);

  if (sections.length === 0) return null;

  const visibleSections =
    selectedSection === "all"
      ? sections
      : sections.filter((section) => section.id === selectedSection);

  /** 7 dias: 3 colunas; 15: 2; 30: 1. Mobile permanece com 1 coluna. */
  const webGridColumns =
    rangeDays === 7
      ? "repeat(3, minmax(0, 1fr))"
      : rangeDays === 15
        ? "repeat(2, minmax(0, 1fr))"
        : "minmax(0, 1fr)";

  return (
    <Stack
      spacing={2}
      sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
    >
      {/* Filtros à esquerda e seletor Linha/Barras à direita, na mesma faixa. */}
      <Stack
        direction="row"
        spacing={{ xs: 0.75, sm: 1 }}
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          justifyContent: "space-between",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowX: "auto",
            overflowY: "hidden",
            pb: 0.25,
            scrollbarWidth: "thin",
          }}
          aria-label="Filtrar gráficos por seção"
        >
          <Button
            size="small"
            variant={selectedSection === "all" ? "contained" : "outlined"}
            onClick={() => setSelectedSection("all")}
            sx={{ flexShrink: 0, borderRadius: 5, px: 2, textTransform: "none" }}
          >
            Todos
          </Button>
          {sections.map((section) => (
            <Button
              key={section.id}
              size="small"
              variant={
                selectedSection === section.id ? "contained" : "outlined"
              }
              onClick={() => setSelectedSection(section.id)}
              sx={{
                flexShrink: 0,
                borderRadius: 5,
                px: 2,
                textTransform: "none",
              }}
            >
              {section.title}
            </Button>
          ))}
        </Stack>

        <Box sx={{ flexShrink: 0, minWidth: 0 }}>{chartStyleControl}</Box>
      </Stack>

      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Médias do período. Clique em um dia do gráfico para ver os dados
        detalhados daquela data.
      </Typography>

      {visibleSections.map((section) => (
        <Paper
          key={section.id}
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "neutral300.main",
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              px: { xs: 1.5, sm: 2.5 },
              py: 1.5,
              minWidth: 0,
              borderRadius: "12px 12px 0 0",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              borderBottom: "1px solid",
              borderColor: "neutral300.main",
            }}
          >
            <section.Icon
              sx={{ fontSize: 24, color: "primary800.main" }}
              aria-hidden
            />
            <Typography
              sx={{
                fontSize: "1.0625rem",
                fontWeight: 700,
                minWidth: 0,
                overflowWrap: "anywhere",
              }}
            >
              {section.title}
            </Typography>
          </Stack>

          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              display: "grid",
              gap: { xs: 1.5, sm: 2 },
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: webGridColumns,
              },
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            {section.series.map((series) => (
              <MetricTrendCard
                key={series.key}
                series={series}
                rangeDays={rangeDays}
                chartStyle={chartStyle}
                onSelectDay={onSelectDay}
              />
            ))}
          </Box>
        </Paper>
      ))}
    </Stack>
  );
}
